import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, StatCard, PageHeader, Spinner, Badge } from "@/components/ui";
import { Users, AlertTriangle, ListChecks, ShieldCheck, TrendingUp, ClipboardList, ArrowRight } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { useBrandPalette } from "@/lib/brand";

const RISK_COLORS = { High: "#dc2626", Rising: "#f59e0b", Low: "#4f46e5" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const navigate = useNavigate();
  const { palette, primary } = useBrandPalette();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/tasks", { params: { priority: "Urgent" } }).then((r) => setTasks(r.data.slice(0, 6)));
  }, []);

  if (!data) return <Spinner label="Loading dashboard…" />;

  return (
    <div className="vc-fade-up">
      <PageHeader
        title="Population Health Command Center"
        subtitle="Real-time view of your managed population across behavioral health, IDD, TBI, and physical health domains."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-members" label="Total Members" value={data.total_members.toLocaleString()} sub="Actively managed" icon={Users} tone="brand" />
        <StatCard testId="stat-high-risk" label="High Risk" value={data.high_risk} sub={`${data.rising_risk} rising risk`} icon={AlertTriangle} tone="red" />
        <StatCard testId="stat-tasks" label="Open Tasks" value={data.open_tasks} sub={`${data.urgent_tasks} urgent`} icon={ListChecks} tone="amber" />
        <StatCard testId="stat-um" label="Pending Authorizations" value={data.pending_um} sub="Awaiting review" icon={ShieldCheck} tone="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-1">Risk Stratification</h3>
          <p className="text-xs text-muted-foreground mb-4">Distribution of managed population by acuity</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.risk_distribution} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                {data.risk_distribution.map((e) => <Cell key={e.name} fill={RISK_COLORS[e.name]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-1">Plan Enrollment</h3>
          <p className="text-xs text-muted-foreground mb-4">Members by benefit plan</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.plan_distribution}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.plan_distribution.map((e, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-1">Health Domains</h3>
          <p className="text-xs text-muted-foreground mb-4">Members served by domain</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.domain_distribution} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} fill={primary} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-lg">Urgent Work Queue</h3>
              <p className="text-xs text-muted-foreground">Priority actions requiring attention</p>
            </div>
            <button onClick={() => navigate("/work-queue")} className="text-sm font-semibold text-brand-700 hover:text-brand-800 flex items-center gap-1" data-testid="view-work-queue">
              View all <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-2">
            {tasks.length === 0 && <p className="text-sm text-muted-foreground py-4">No urgent tasks. </p>}
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 rounded-md border border-border p-3 hover:bg-muted transition-colors">
                <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.title}</p>
                  <p className="text-xs text-muted-foreground">{t.member_name} · Due {t.due_date}</p>
                </div>
                <Badge tone={t.priority}>{t.priority}</Badge>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-4">
          <StatCard label="Open Care Gaps" value={data.open_care_gaps} sub="Across population" icon={TrendingUp} tone="amber" />
          <StatCard label="Assessment Completion" value={`${data.assessment_completion}%`} sub="Comprehensive assessments" icon={ClipboardList} tone="brand" />
          <Card className="p-6">
            <h3 className="font-display font-semibold mb-3">Top Counties</h3>
            <div className="space-y-2">
              {data.county_distribution.slice(0, 5).map((c) => (
                <div key={c.name} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{c.name}</span>
                  <span className="font-mono font-semibold">{c.value}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
